import React, { useMemo, useRef, useState } from 'react';

function Shape({ shape }) {
  const style = {
    position: 'absolute',
    left: `${shape.x}%`,
    top: `${shape.y}%`,
    width: shape.size,
    height: shape.size,
    background: shape.color,
    transform: 'translate(-50%, -50%)',
    borderRadius: shape.kind === 'circle' ? '9999px' : shape.kind === 'triangle' ? 0 : 3,
    clipPath: shape.kind === 'triangle' ? 'polygon(50% 0, 100% 100%, 0 100%)' : undefined,
  };
  return <div style={style} />;
}

function FoundRings({ diffs, foundSet, shiftX = 0 }) {
  return diffs.map((d) => (
    foundSet.has(d.id) ? (
      <div
        key={`ring-${d.id}-${shiftX}`}
        className="absolute rounded-full border-2 border-[#f76f4b] pointer-events-none"
        style={{
          left: `${Math.min(96, d.x + shiftX)}%`,
          top: `${d.y}%`,
          width: 26,
          height: 26,
          transform: 'translate(-50%, -50%)',
        }}
      />
    ) : null
  ));
}

export default function DifferenceBoard({ payload, savedFound, onSolved, onProgress }) {
  const shapes = payload?.shapes || payload?.base || [];
  const diffs = payload?.diffs || [];
  const photo = payload?.image;
  const photoAlt = payload?.image_alt;
  const photoMode = !!(photo && photoAlt);
  const [found, setFound] = useState(() => [...(savedFound || [])]);
  const foundRef = useRef([...(savedFound || [])]);
  const solvedRef = useRef(false);

  const foundSet = useMemo(() => new Set(found), [found]);
  const diffIds = useMemo(() => new Set(diffs.map((d) => d.id)), [diffs]);

  const mark = (id) => {
    if (!diffIds.has(id) || foundRef.current.includes(id)) return;
    const next = [...foundRef.current, id];
    foundRef.current = next;
    setFound(next);
    onProgress?.({ found: next });
    if (diffs.every((d) => next.includes(d.id)) && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.();
    }
  };

  const onClick = (e, panel) => {
    const rect = panel.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const hit = diffs.find((d) => {
      const radius = Math.max(d.r || 3, 2.6);
      const dx = (d.x - x) * rect.width;
      const dy = (d.y - y) * rect.height;
      return Math.hypot(dx, dy) < radius * rect.width * 1.15;
    });
    if (hit) mark(hit.id);
  };

  const rightShapes = shapes.map((s) => (
    diffIds.has(s.id) ? { ...s, x: Math.min(96, s.x + 7), color: '#9ca3af' } : s
  ));
  const credit = payload?.credit?.source;

  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">
        Trouvez les {diffs.length} différences — {found.length}/{diffs.length}
      </p>
      {photoMode ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[photo, photoAlt].map((src, idx) => (
            <button
              key={idx}
              type="button"
              className="relative rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden cursor-crosshair p-0 bg-slate-50 dark:bg-gray-900"
              onClick={(e) => onClick(e, e.currentTarget)}
            >
              <img
                src={src}
                alt={idx === 0 ? 'Image A' : 'Image B'}
                className="block w-full h-auto select-none pointer-events-none"
                draggable={false}
              />
              <FoundRings diffs={diffs} foundSet={foundSet} />
            </button>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[shapes, rightShapes].map((panelShapes, idx) => (
            <div
              key={idx}
              className="relative h-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-900 cursor-crosshair overflow-hidden"
              onClick={(e) => onClick(e, e.currentTarget)}
            >
              {panelShapes.map((s) => <Shape key={`${idx}-${s.id}`} shape={s} />)}
              <FoundRings diffs={diffs} foundSet={foundSet} shiftX={idx === 1 ? 7 : 0} />
            </div>
          ))}
        </div>
      )}
      {credit && (
        <p className="text-[10px] text-gray-400 mt-2">Photo libre de droit — {credit}</p>
      )}
    </div>
  );
}
